import React from 'react';
import math from 'polished/lib/math/math';
import typography from '../utils/typography';
import { useTheme } from '@veneer/theme';
import { SerializedStyles, css } from '@emotion/core';
import getPillBorder from '../utils/get_pill_border';

type StylesType = {
  root: {
    base: SerializedStyles;
    hover: {
      base: SerializedStyles;
      hc: SerializedStyles;
    };
    error: {
      base: SerializedStyles;
      hover: {
        base: SerializedStyles;
        hc: SerializedStyles;
      };
    };
    focusedOrOpen: {
      base: SerializedStyles;
      hc: SerializedStyles;
    };
    open: {
      base: SerializedStyles;
      hc: SerializedStyles;
    };
    disabled: SerializedStyles;
    pill: SerializedStyles;
  };
  button: {
    base: SerializedStyles;
    openOrFocused: SerializedStyles;
    disabled: SerializedStyles;
  };
  placeholder: {
    base: SerializedStyles;
    disabled: SerializedStyles;
  };
  placeholderEllipsis: SerializedStyles;
  buttons: {
    base: SerializedStyles;
    openOrFocused: SerializedStyles;
  };
  clearButton: {
    base: SerializedStyles;
    disabled: SerializedStyles;
  };
  openButton: {
    base: SerializedStyles;
    open: SerializedStyles;
    disabled: SerializedStyles;
    hc: SerializedStyles;
  };
  content: {
    base: SerializedStyles;
    upwards: SerializedStyles;
    hc: SerializedStyles;
    hcFocusedOrOpen: SerializedStyles;
    pill: SerializedStyles;
  };
  searchDiv: {
    base: SerializedStyles;
    upwards: SerializedStyles;
  };
  searchInput: SerializedStyles;
  searchIcon: SerializedStyles;
  noResults: SerializedStyles;
  showingResultsDivider: SerializedStyles;
  buttonTitle: SerializedStyles;
  titleIcon: SerializedStyles;
  titleLabel: SerializedStyles;
  titleLabelEllipsis: SerializedStyles;
};

const useStyles = (): StylesType => {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      root: {
        base: css({
          alignItems: 'center',
          backgroundColor: 'transparent',
          borderColor: theme.color.neutral.base.default,
          borderRadius: theme.borderRadius,
          borderStyle: 'solid',
          borderWidth: '1px',
          display: 'flex',
          minWidth: 114,
          position: 'relative',
          transition:
            'background-color 150ms ease-in, border-color 150ms ease-in',
          willChange: 'background-color, border-color',
        }),

        hover: {
          base: css({
            '&:hover': {
              backgroundColor: theme.color.neutral.background.hover,
              borderColor: theme.color.neutral.base.hover,
            },
          }),
          hc: css({
            '&:hover': {
              backgroundColor: theme.color.neutral.background.default,
            },
          }),
        },

        error: {
          base: css({
            borderColor: theme.color.negative.base.default,
          }),
          hover: {
            base: css({
              '&:hover': {
                backgroundColor: theme.color.neutral.background.hover,
                borderColor: theme.color.negative.base.hover,
              },
            }),
            hc: css({
              '&:hover': {
                backgroundColor: theme.color.neutral.background.default,
              },
            }),
          },
        },

        focusedOrOpen: {
          base: css({
            borderWidth: '2px',
            backgroundColor: theme.color.background.container,
            borderColor: theme.color.primary.base.default,
          }),
          hc: css({
            borderColor: theme.color.neutral.base.active,
          }),
        },
        open: {
          base: css({
            borderColor: theme.color.primary.base.default,
          }),
          hc: css({
            borderColor: theme.color.primary.base.active,
          }),
        },
        disabled: css({
          borderColor: theme.color.foreground.disabled,
        }),

        pill: css({
          borderRadius: getPillBorder,
        }),
      },
      button: {
        base: css({
          // this is related to select multiple selected options showing on input
          color: theme.color.foreground.default,
          cursor: 'pointer',
          display: 'inline-flex',
          overflow: 'hidden',
          padding: `calc(${theme.orbitalIntermediate} - 1px) calc(${theme.orbitalThree} - 1px)`,
          paddingRight: theme.orbitalThree,
          textOverflow: 'ellipsis',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          width: '100%',
        }),
        openOrFocused: css({
          padding: `calc(${theme.orbitalIntermediate} - 2px) calc(${theme.orbitalThree} - 2px)`,
        }),

        disabled: css({
          color: theme.color.foreground.disabled,
          cursor: 'unset',
        }),
      },
      placeholder: {
        base: css({
          color: theme.color.foreground.light,
          display: 'flex',
          margin: '2px 0',
          overflow: 'hidden',
          transition: 'opacity 100ms cubic-bezier(0, 0, 0.2, 1)',
        }),
        disabled: css({
          color: theme.color.foreground.disabled,
        }),
      },
      placeholderEllipsis: css({
        display: 'inline-block',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      }),
      buttons: {
        base: css({
          display: 'inline-flex',
          paddingRight: `calc(${theme.orbitalTwo} - 1px)`,
        }),
        openOrFocused: css({
          paddingRight: `calc(${theme.orbitalTwo} - 2px)`,
        }),
      },

      clearButton: {
        base: css({
          marginRight: theme.orbitalOne,
          '> svg': {
            fill: theme.color.neutral.base.default,
          },
        }),

        disabled: css({
          '> svg': {
            fill: theme.color.foreground.disabled,
          },
        }),
      },
      openButton: {
        base: css({
          padding: theme.layout.size1,
          transition: 'transform 150ms cubic-bezier(0, 0, 0.2, 1)',
          willChange: 'transform',
          alignItems: 'center',
          cursor: 'pointer',
          display: 'inline-flex',
          '> svg': {
            fill: theme.color.foreground.light,
          },
        }),
        open: css({
          transform: 'rotate(180deg)',
        }),
        hc: css({
          '> svg': {
            fill: theme.color.primary.base.default,
          },
        }),
        disabled: css({
          cursor: 'unset',
          '> svg': {
            fill: theme.color.foreground.disabled,
          },
        }),
      },
      // Popover
      content: {
        base: css({
          marginTop: theme.orbitalTwo,
          backgroundColor: theme.color.background.container,
          borderRadius: theme.borderRadius4,
          boxShadow: `${theme.elevation.level16.boxShadow} ${theme.color.effects.dropShadow}`,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minWidth: '100%',
          overflow: 'hidden',
        }),

        upwards: css({
          marginBottom: theme.orbitalTwo,
          flexDirection: 'column-reverse',
        }),
        hc: css({
          borderColor: theme.color.primary.base.active,
          borderStyle: 'solid',
          borderWidth: '2px',
        }),
        hcFocusedOrOpen: css({
          borderWidth: '2px',
        }),
        pill: css({
          borderRadius: theme.borderRadius9,
        }),
      },
      searchDiv: {
        base: css({
          position: 'relative',
          borderBottom: `1px solid ${theme.color.stroke.default}`,
        }),
        upwards: css({
          borderTop: `1px solid ${theme.color.stroke.default}`,
        }),
      },
      searchInput: css({
        ...typography(theme).label,
        background: 'transparent',
        border: 0,
        boxSizing: 'border-box',
        color: theme.color.foreground.default,
        margin: 0,
        padding: theme.layout.size3,
        paddingRight: math(`4 * ${theme.layout.size3}`),
        width: '100%',

        '::placeholder': {
          color: theme.color.foreground.light,
          opacity: 1 /* Firefox */,
        },

        /* Safari compatibility */
        opacity: 1,
        WebkitTextFillColor: 'currentColor',

        '&:focus': {
          outline: 0,
        },
      }),
      searchIcon: css({
        fill: theme.color.foreground.light,
        pointerEvents: 'none',
        position: 'absolute',
        right: theme.orbitalFour,
        top: '50%',
        transform: 'translateY(-50%)',
      }),
      noResults: css({
        ...typography(theme).label,
        boxSizing: 'border-box',
        color: theme.color.foreground.medium,
        display: 'block',
        padding: [`calc(${theme.layout.size4} + 2px)`, theme.layout.size2].join(
          ' '
        ),
        textAlign: 'center',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      }),
      showingResultsDivider: css({
        border: '0 !important',
        paddingTop: '0 !important',
        paddingBottom: theme.layout.size2,
      }),
      buttonTitle: css({
        margin: '2px 0',
        display: 'block',
      }),
      titleIcon: css({
        alignItems: 'center',
        display: 'flex',
        svg: { marginRight: theme.orbitalTwo },
      }),
      titleLabel: css({
        display: 'inline-block',
        margin: '2px 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: theme.color.foreground.default,
      }),
      titleLabelEllipsis: css({
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        margin: '2px 0',
      }),
    };
  }, [theme]);
};

export default useStyles;
